/**
 * Basecamp-Inspired Theme
 *
 * Warm, friendly, and approachable design inspired by Basecamp's visual language.
 * Features warm greens, rounded corners, and comfortable spacing.
 */

import type { AppTheme } from './types';

export const basecampTheme: AppTheme = {
  name: 'basecamp',
  displayName: 'Basecamp',
  description: 'Warm, friendly theme inspired by Basecamp',

  antdTheme: {
    token: {
      colorPrimary: '#1d8649',
      colorSuccess: '#3eb370',
      colorWarning: '#e3a735',
      colorError: '#dd5f5f',
      colorInfo: '#4d9ed4',
      colorLink: '#1d8649',

      fontSize: 15,
      fontSizeHeading1: 32,
      fontSizeHeading2: 26,
      fontSizeHeading3: 22,
      fontSizeHeading4: 18,
      fontSizeHeading5: 16,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      borderRadius: 8,
      borderRadiusLG: 12,
      borderRadiusSM: 6,
      borderRadiusXS: 4,

      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#f7f5f1',
      colorBorder: '#e8e4dc',
      colorBorderSecondary: '#f0ece4',

      colorText: '#2e2d2a',
      colorTextSecondary: '#6b6963',
      colorTextTertiary: '#9e9b94',
      colorTextQuaternary: '#c4c1ba',

      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      boxShadowSecondary: '0 1px 4px rgba(0, 0, 0, 0.06)',
    },

    components: {
      Layout: {
        headerBg: '#1d8649',
        headerColor: '#ffffff',
        headerHeight: 58,
        headerPadding: '0 24px',
        siderBg: '#2e2d2a',
        bodyBg: '#f7f5f1',
      },

      Button: {
        borderRadius: 8,
        borderRadiusLG: 10,
        borderRadiusSM: 6,
        controlHeight: 40,
        controlHeightLG: 48,
        controlHeightSM: 32,
        fontWeight: 600,
        primaryShadow: '0 2px 0 rgba(29, 134, 73, 0.1)',
      },

      Card: {
        borderRadius: 12,
        borderRadiusLG: 16,
        colorBgContainer: '#ffffff',
        boxShadowTertiary: 'none',
        padding: 24,
        paddingLG: 32,
      },

      Table: {
        headerBg: '#f9f8f6',
        headerColor: '#9e9b94',
        headerBorderRadius: 8,
        borderRadius: 8,
        rowHoverBg: '#faf9f7',
        cellPaddingBlock: 14,
        cellPaddingInline: 16,
      },

      Input: {
        borderRadius: 8,
        controlHeight: 40,
        controlHeightLG: 48,
        paddingBlock: 10,
        paddingInline: 16,
      },

      Select: {
        borderRadius: 8,
        controlHeight: 40,
        controlHeightLG: 48,
      },

      Tag: {
        borderRadiusSM: 6,
        fontSizeSM: 13,
      },

      Modal: {
        borderRadiusLG: 16,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      },

      Drawer: {
        colorBgElevated: '#ffffff',
      },

      Badge: {
        fontSize: 13,
      },

      Breadcrumb: {
        fontSize: 14,
        itemColor: '#9e9b94',
        linkColor: '#1d8649',
      },

      Segmented: {
        borderRadius: 8,
        itemSelectedBg: '#1d8649',
        itemSelectedColor: '#ffffff',
      },

      Tabs: {
        itemColor: '#9e9b94',
        itemSelectedColor: '#2e2d2a',
        itemHoverColor: '#6b6963',
      },

      Dropdown: {
        borderRadiusLG: 10,
      },

      Tooltip: {
        borderRadius: 8,
      },

      Divider: {
        colorSplit: '#f0ece4',
      },
    },
  },

  cssVariables: {
    '--layout-header-bg': '#1d8649',
    '--layout-sidebar-bg': '#2e2d2a',
    '--layout-content-bg': '#f7f5f1',

    '--surface-0': '#f7f5f1',
    '--surface-1': '#ffffff',
    '--surface-2': '#ffffff',
    '--surface-3': '#ffffff',

    '--text-primary': '#2e2d2a',
    '--text-secondary': '#6b6963',
    '--text-tertiary': '#9e9b94',
    '--text-muted': '#c4c1ba',

    '--border-default': '#e8e4dc',
    '--border-subtle': '#f0ece4',
    '--border-strong': '#d4d0c8',

    '--border-color': '#e8e4dc',
    '--border-radius': '8px',

    '--shadow-sm': '0 1px 4px rgba(0, 0, 0, 0.06)',
    '--shadow-md': '0 2px 8px rgba(0, 0, 0, 0.08)',
    '--shadow-lg': '0 8px 24px rgba(0, 0, 0, 0.12)',

    '--accent-warm': '#e3a735',
    '--accent-cool': '#4d9ed4',

    '--status-success': '#3eb370',
    '--status-warning': '#e3a735',
    '--status-error': '#dd5f5f',
    '--status-info': '#4d9ed4',

    '--status-success-subtle': '#ecfdf3',
    '--status-warning-subtle': '#fffbeb',
    '--status-error-subtle': '#fef2f2',
    '--status-info-subtle': '#eff6ff',

    '--control-bg': '#f9f8f6',
    '--control-border': '#e8e4dc',
    '--control-hover': '#f5f3ef',

    '--hover-bg': '#faf9f7',
    '--active-bg': '#f5f3ef',
  },
};
